/* public form component.
   Standalone & embeddable: owns form state, persistence, theming and the intake-API
   integration, and renders the frame-less responsive flow. */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { FlowApp } from "./flow/FlowApp.jsx";
import { getFlowProfile } from "./data/profiles.js";
import { themeVars, ensureFonts } from "./theme.js";
import { setResources } from "./resources.js";
import { createIntakeClient, newApplicationId } from "./intake/IntakeClient.js";

export function ApplicationForm({
  product = "car",
  channel = "direct",         // "direct" | "broker" | "internal" - flow profile hint; the stored channel is set server-side
  direction = "sectioned",
  accent = "brand",              // named preset or hex key in ACCENTS; default = brand.config.js theme
  font = "brand",               // named preset in FONTS; default = brand.config.js theme
  mascot = "milestones",
  height = "auto",            // "auto" (flows in page) | "100%"/"fill" | "640px" | number(px)
  persist = true,
  storageKey = "app_form_v1",
  fonts = true,              // inject Google Fonts <link> (set false if self-hosting)
  logoUrl,
  mascotUrl,
  initialValues,
  applicationId: applicationIdProp,
  submitUrl,                  // intake endpoint (default: /api/v1/applications)
  initiateUrl,                // bank-session endpoint (default: derived from submitUrl → /api/v1/bank-session)
  resume,                     // { applicationId, resumeToken } - hydrate a server-side draft (magic link)
  attribution,                // { refsource, utm_source, utm_medium, utm_campaign, utm_content, landing_page }
  intake,                       // client config (see IntakeClient.js)
  onSubmit,                   // optional override: (form, { applicationId, product, attribution }) => Promise<{reference?}>
  onStepComplete,            // optional: (stepId, form) => void
  onChange,                  // optional: (form) => void  (fires on every change)
} = {}) {
  // one-time brand-asset overrides (white-labelling)
  useMemo(() => { if (logoUrl || mascotUrl) setResources({ ...(logoUrl ? { logo: logoUrl } : {}), ...(mascotUrl ? { mascot: mascotUrl } : {}) }); }, [logoUrl, mascotUrl]);

  // Flow profile: the product+channel bundle of steps / initial state / copy.
  const profile = useMemo(() => getFlowProfile({ product, channel }), [product, channel]);

  // Intake client — created once from the mount-time config.
  // submitUrl precedence: explicit prop > intake.submitUrl > default (/api/v1/applications)
  const clientRef = useRef(null);
  if (!clientRef.current) {
    const intakeCfg = { ...(intake || {}) };
    if (submitUrl) intakeCfg.submitUrl = submitUrl;
    if (initiateUrl) intakeCfg.initiateUrl = initiateUrl;
    clientRef.current = createIntakeClient(intakeCfg);
  }
  const client = clientRef.current;

  // form state (hydrate from storage, then apply any initialValues)
  const [f, setF] = useState(() => {
    let base = JSON.parse(JSON.stringify(profile.initial));
    if (persist && typeof localStorage !== "undefined") {
      try { const s = localStorage.getItem(storageKey); if (s) base = { ...base, ...JSON.parse(s) }; } catch (e) { /* ignore */ }
    }
    if (initialValues) base = { ...base, ...initialValues };
    return base;
  });

  // Stable correlation id (a UUID) — keys the server draft, the illion
  // statements slug/webhook match and the console file, and is shared by the
  // Step 6 session and the Step 9 final submission.
  // Prefer: explicit prop > id persisted with the draft (so the Step-6
  // session survives a refresh) > a fresh UUID.
  const appIdRef = useRef(null);
  if (!appIdRef.current) appIdRef.current = applicationIdProp || f._applicationId || newApplicationId();
  const applicationId = appIdRef.current;
  const set = (patch) => setF((prev) => { const next = { ...prev, ...patch }; if (onChange) { try { onChange(next); } catch (e) {} } return next; });
  const replace = (next) => setF(() => {
    // a full replace without a carried application id is a fresh application
    // ("start new") — mint a new id so it can't upsert over the previous one
    if (!next._applicationId) appIdRef.current = newApplicationId();
    if (onChange) { try { onChange(next); } catch (e) {} }
    return next;
  });

  // debounced persistence
  useEffect(() => {
    if (!persist || typeof localStorage === "undefined") return;
    const id = setTimeout(() => { try { localStorage.setItem(storageKey, JSON.stringify(f)); } catch (e) {} }, 300);
    return () => clearTimeout(id);
  }, [f, persist, storageKey]);

  // fonts
  useEffect(() => { if (fonts) ensureFonts(font); }, [fonts, font]);

  // Resume from a server-side draft (magic link: ?resume=<id>&rt=<token>).
  // The server copy wins over any local draft; if the link is invalid/expired
  // the form simply starts fresh.
  const [resuming, setResuming] = useState(!!(resume && resume.applicationId && resume.resumeToken));
  const [resumeAfterStepId, setResumeAfterStepId] = useState(null);
  useEffect(() => {
    if (!resuming) return;
    let cancelled = false;
    (async () => {
      const d = await client.fetchDraft(resume);
      if (!cancelled) {
        if (d && d.form && typeof d.form === "object") {
          replace({
            ...JSON.parse(JSON.stringify(profile.initial)), ...d.form,
            _applicationId: d.applicationId, _resumeToken: resume.resumeToken,
          });
          if (d.currentStep) setResumeAfterStepId(d.currentStep);
        }
        setResuming(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // The application id actually in force: once the Step-6 session exists,
  // the id persisted with the draft wins so the statements session and the
  // final submission always land on one application file.
  const currentAppId = (form) => form._applicationId || applicationId;

  // Save state for the calm, always-visible indicator (spec §10.5): Saving… /
  // Saved just now / Unable to save — retrying. NEVER claims "saved" unless
  // the request succeeded; a device-only draft says so honestly.
  const [saveState, setSaveState] = useState({ state: "idle", at: null });
  const retryRef = useRef(null);

  // Server-side draft sync (progressive capture). Fires after each completed
  // step: the first save (end of Step 1) registers the application
  // server-side ("lead") and returns a resume token that is persisted with
  // the local draft. Failures never disturb the applicant's flow — the
  // indicator says "retrying", and one retry fires 5s later.
  const syncDraft = async (stepId, form, isRetry = false) => {
    clearTimeout(retryRef.current);
    setSaveState({ state: "saving", at: Date.now() });
    try {
      const r = await client.saveDraft({
        applicationId: currentAppId(form),
        resumeToken: form._resumeToken || undefined,
        product, channel, step: stepId, form, attribution,
      });
      if (r && r.failed) {
        // The client reports failures as a value, never a throw (the flow
        // must not be disturbed) — the indicator still has to tell the truth.
        setSaveState({ state: "error", at: Date.now() });
        if (!isRetry) retryRef.current = setTimeout(() => { syncDraft(stepId, form, true).catch(() => {}); }, 5000);
      } else if (r && !r.simulated) {
        const patch = {};
        if (r.applicationId && r.applicationId !== form._applicationId) patch._applicationId = r.applicationId;
        if (r.resumeToken && !form._resumeToken) patch._resumeToken = r.resumeToken;
        if (Object.keys(patch).length) set(patch);
        setSaveState({ state: "saved", at: Date.now() });
      } else {
        // No backend configured: the draft lives in this browser only.
        setSaveState({ state: persist ? "local" : "idle", at: Date.now() });
      }
    } catch (e) {
      setSaveState({ state: "error", at: Date.now() });
      if (!isRetry) retryRef.current = setTimeout(() => { syncDraft(stepId, form, true).catch(() => {}); }, 5000);
    }
  };
  useEffect(() => () => clearTimeout(retryRef.current), []);

  const handleStepComplete = (stepId, form) => {
    syncDraft(stepId, form).catch(() => {});
    if (onStepComplete) { try { onStepComplete(stepId, form); } catch (e) {} }
  };
  // Step 6: create the lender application and fetch the bank-statements URL.
  const handleInitiate = async (form) => {
    const r = await client.initiate({ applicationId: currentAppId(form), product, channel, form, attribution });
    const patch = {};
    if (r && r.applicationId) patch._applicationId = r.applicationId;
    if (r && r.lenderReference && !r.simulated) patch.lenderReference = r.lenderReference;
    if (r && r.bankStatementsUrl) patch.bankStatementsUrl = r.bankStatementsUrl;
    if (Object.keys(patch).length) set(patch);
    return r;
  };
  // Step 9: final submission — same application id, full payload.
  const handleSubmit = async (form) => {
    const id = currentAppId(form);
    if (onSubmit) return onSubmit(form, { applicationId: id, product, channel, attribution });
    return client.submit({ applicationId: id, product, channel, form, attribution });
  };

  const wrapperStyle = {
    ...themeVars(accent, font),
    width: "100%",
    ...(height === "auto" || height == null
      ? {}
      : { height: height === "fill" ? "100%" : (typeof height === "number" ? `${height}px` : height), overflow: "auto" }),
  };

  if (resuming) {
    return (
      <div className="pf-embed-root" style={wrapperStyle}>
        <div className="gd" style={{ minHeight: 320, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, background: "var(--bg)" }}>
          <span className="pf-spin" style={{ width: 44, height: 44, border: "4px solid var(--field-2)", borderTopColor: "var(--coral)", borderRadius: "50%" }} />
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>Picking up where you left off…</div>
        </div>
      </div>
    );
  }

  return (
    <div className={"pf-embed-root" + (height && height !== "auto" ? " pf-scroll" : "")} style={wrapperStyle}>
      <FlowApp
        f={f} set={set} replace={replace}
        direction={direction} mascot={mascot} product={product} profile={profile}
        saveState={saveState}
        onStepComplete={handleStepComplete} onSubmit={handleSubmit}
        onInitiateBankSession={handleInitiate}
        onBankSessionStatus={(form) => client.bankSessionStatus({ applicationId: currentAppId(form) })}
        onBankSessionEvent={(form, event) => client.relayBankEvent({ applicationId: currentAppId(form), event })}
        onAbnLookup={(abn) => client.lookupAbn(abn)}
        onSupplyStatements={(form, payload) => client.supplyStatements({ applicationId: currentAppId(form), ...payload })}
        resumeAfterStepId={resumeAfterStepId}
      />
    </div>
  );
}

export default ApplicationForm;
